'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { EventoPublicado, Funcion } from '@/types';

interface EventoCardProps {
  evento: EventoPublicado;
  funcion: Funcion;
  priority?: boolean;
}

export function parseFechaHora(fechaCompleta: Date) {
  const dia = fechaCompleta
    .toLocaleDateString('es-AR', { day: '2-digit' })
    .padStart(2, '0');

  const mes = fechaCompleta
    .toLocaleDateString('es-AR', { month: 'short' })
    .replace('.', '');

  const anio = fechaCompleta.getFullYear().toString();

  const hora = fechaCompleta
    .toLocaleTimeString('es-AR', { hour: '2-digit', hour12: false });

  const minuto = fechaCompleta
    .toLocaleTimeString('es-AR', { minute: '2-digit' });

  return { dia, mes, anio, hora, minuto };
}

export default function EventoCard({ evento, funcion, priority = false }: EventoCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  // Referencia para medir el párrafo del título
  const titleRef = useRef<HTMLParagraphElement>(null);

  const { dia, mes, anio, hora, minuto } = parseFechaHora(
    new Date(`${funcion.fecha}T${funcion.hora}`)
  );

  const isAgotado = funcion.status === 'agotado';

  // Efecto que mide si el texto es más alto que el espacio disponible
  useEffect(() => {
    if (titleRef.current) {
      const { scrollHeight, clientHeight } = titleRef.current;
      // Si la altura real (scrollHeight) es mayor a la altura visible (clientHeight), está cortado
      setIsTruncated(scrollHeight > clientHeight);
    }
  }, [evento.nombre]);

  // --- RENDERIZADO PARA EVENTOS AGOTADOS ---
  if (isAgotado) {
    return (
      <div className="cursor-not-allowed sm:w-full rounded-xl flex flex-col overflow-hidden opacity-75 shadow-sm border border-gray-100">
        <div className="relative rounded-t-xl w-full aspect-[4/5] bg-black">
          <Image
            src={evento.flyer || '/bebop/placeholder/flyer_ticketera.png'}
            alt={evento.nombre}
            fill
            className="object-contain rounded-t-sm opacity-60"
            sizes="(max-width: 768px) 100vw, 25vw"
            priority={priority}
            loading={priority ? undefined : 'lazy'}
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-t-xl">
            <div className="bg-red-600 text-white px-4 py-2 rounded font-bold text-xl uppercase tracking-wider shadow-lg">
              Sold Out
            </div>
          </div>
        </div>

        <div className="rounded-b-xl bg-white flex flex-col flex-grow p-5 border-t border-gray-100">
          <div className="flex flex-col flex-grow justify-start min-h-[5rem]">
            <p className="uppercase text-[1.1rem] leading-snug font-bold text-zinc-900 line-clamp-3">
              {evento.nombre}
            </p>
          </div>

          <div className="flex items-end justify-start gap-6 mt-4 pt-4 border-t border-gray-100">
            {/* FECHA */}
            <div className="flex items-end gap-1">
              <span className="text-4xl font-display leading-none text-gray-400">{dia}</span>
              <div className="flex flex-col leading-none pb-0.5">
                <span className="uppercase text-[11px] font-bold tracking-wide text-gray-400">{mes}</span>
                <span className="text-[10px] font-semibold text-gray-400">{anio}</span>
              </div>
            </div>

            {/* HORA */}
            <div className="flex items-end gap-1">
              <span className="text-4xl font-display leading-none text-gray-400">{hora}</span>
              <div className="flex flex-col leading-none pb-0.5">
                <span className="text-[11px] font-display font-bold text-gray-400">{minuto == '0' ? '00' : minuto}</span>
                <span className="text-[9px] uppercase tracking-wide text-gray-400">hrs</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDERIZADO PARA EVENTOS DISPONIBLES ---
  return (
    <div className="sm:w-full rounded-xl flex flex-col overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 bg-white h-full border border-gray-100 group">

      {/* IMAGEN CLIQUEABLE (Te lleva al evento) */}
      <Link href={`/evento/${evento.id}`} aria-label={`Ver evento ${evento.nombre}`} className="relative rounded-t-xl w-full aspect-[4/5] bg-black block overflow-hidden">
        <Image
          src={evento.flyer || '/bebop/placeholder/flyer_ticketera.png'}
          alt={evento.nombre}
          fill
          className="object-contain rounded-t-sm transition-transform duration-500 group-hover:scale-105"
          sizes="(max-width: 768px) 100vw, 25vw"
          priority={priority}
          loading={priority ? undefined : 'lazy'}
        />
      </Link>

      {/* CUERPO DE LA TARJETA */}
      <div className="flex flex-col flex-grow p-5">

        {/* Título con lógica de expansión */}
        <div className="flex flex-col flex-grow justify-start mb-4">
          <p
            ref={titleRef}
            className={`uppercase text-[1.1rem] leading-snug font-bold text-zinc-900 transition-all duration-300 ${isExpanded ? '' : 'line-clamp-3'}`}
          >
            {evento.nombre}
          </p>

          {/* El botón ahora solo se dibuja si isTruncated es true */}
          {isTruncated && (
            <button
              onClick={(e) => {
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }}
              className="text-[11px] font-bold uppercase tracking-wider text-red-600 hover:text-red-800 mt-2 text-left w-fit cursor-pointer transition-colors"
            >
              {isExpanded ? 'Ver menos -' : 'Ver más info +'}
            </button>
          )}
        </div>

        {/* CONTENEDOR INFERIOR: Fecha y Botón de Comprar alineados a la base */}
        <div className="mt-auto flex flex-col gap-4 pt-4 border-t border-gray-100">

          {/* Fecha y hora */}
          <div className="flex items-end justify-between">
            <div className="flex items-end justify-start gap-4">
              {/* FECHA */}
              <div className="flex items-end gap-1">
                <span className="text-4xl font-display leading-none text-zinc-800">{dia}</span>
                <div className="flex flex-col leading-none pb-0.5">
                  <span className="uppercase text-[11px] font-bold tracking-wide text-zinc-500">{mes}</span>
                  <span className="text-[10px] font-semibold text-zinc-400">{anio}</span>
                </div>
              </div>

              {/* HORA */}
              <div className="flex items-end gap-1">
                <span className="text-4xl font-display leading-none text-zinc-800">{hora}</span>
                <div className="flex flex-col leading-none pb-0.5">
                  <span className="text-[11px] font-display font-bold text-zinc-500">{minuto == '0' ? '00' : minuto}</span>
                  <span className="text-[9px] uppercase tracking-wide text-zinc-400">hrs</span>
                </div>
              </div>
            </div>
          </div>

          {/* BOTÓN DE COMPRAR PRINCIPAL */}
          <Link
            href={`/evento/${evento.id}`}
            className="w-full text-center bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg transition-colors uppercase text-sm tracking-wider shadow-sm hover:shadow-md"
          >
            Comprar Entradas
          </Link>

        </div>
      </div>
    </div>
  );
}